import { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import ReplyIcon from '@mui/icons-material/Reply'
import { format } from 'date-fns'
import { zhTW } from 'date-fns/locale'
import { useDiscussionStore } from '../../stores/discussionStore'
import { Discussion } from '../../models/Discussion'

export default function DiscussionListPage() {
  const {
    discussions,
    replies,
    loading,
    error,
    fetchDiscussions,
    fetchReplies,
    addAdminReply,
    deleteDiscussion,
    deleteReply,
  } = useDiscussionStore()

  const [selectedDiscussion, setSelectedDiscussion] = useState<Discussion | null>(null)
  const [replyContent, setReplyContent] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)

  useEffect(() => {
    fetchDiscussions()
  }, [fetchDiscussions])

  const handleOpenDetail = async (discussion: Discussion) => {
    setSelectedDiscussion(discussion)
    await fetchReplies(discussion.id)
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setSelectedDiscussion(null)
    setReplyContent('')
  }

  const handleReply = async () => {
    if (!selectedDiscussion || !replyContent.trim()) return

    try {
      await addAdminReply({
        discussionId: selectedDiscussion.id,
        content: replyContent.trim(),
      })
      setReplyContent('')
    } catch {
      // Error handled by store
    }
  }

  const handleDelete = async (id: string) => {
    if (window.confirm('確定要刪除此討論嗎？這將同時刪除所有回覆。')) {
      await deleteDiscussion(id)
    }
  }

  const handleDeleteReply = async (discussionId: string, replyId: string) => {
    if (window.confirm('確定要刪除此回覆嗎？')) {
      await deleteReply(discussionId, replyId)
    }
  }

  if (loading && discussions.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
        討論區管理
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {discussions.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <Typography color="text.secondary">暫無討論</Typography>
          </CardContent>
        </Card>
      ) : (
        discussions.map((discussion) => (
          <Card key={discussion.id} sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2">
                    {discussion.isAnonymous ? '匿名' : discussion.authorName}
                  </Typography>
                  {discussion.isAnonymous && (
                    <Chip label="匿名" size="small" />
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {format(discussion.createdAt, 'yyyy/MM/dd HH:mm', { locale: zhTW })}
                  </Typography>
                </Box>
                <Box>
                  <IconButton size="small" onClick={() => handleOpenDetail(discussion)}>
                    <ReplyIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(discussion.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>

              <Typography
                variant="body1"
                sx={{
                  whiteSpace: 'pre-wrap',
                  cursor: 'pointer',
                }}
                onClick={() => handleOpenDetail(discussion)}
              >
                {discussion.content}
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Chip
                  label={`${discussion.likes} 個讚`}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>
        ))
      )}

      {/* Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        {selectedDiscussion && (
          <>
            <DialogTitle>
              討論詳情
              <Typography variant="caption" display="block" color="text.secondary">
                {selectedDiscussion.isAnonymous ? '匿名' : selectedDiscussion.authorName} ·{' '}
                {format(selectedDiscussion.createdAt, 'yyyy/MM/dd HH:mm', { locale: zhTW })}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 3 }}>
                {selectedDiscussion.content}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                回覆 ({replies[selectedDiscussion.id]?.length || 0})
              </Typography>

              <List>
                {replies[selectedDiscussion.id]?.map((reply) => (
                  <ListItem
                    key={reply.id}
                    sx={{
                      bgcolor: reply.isAdminReply ? 'primary.50' : 'transparent',
                      borderRadius: 1,
                      mb: 1,
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2">
                            {reply.authorName}
                          </Typography>
                          {reply.isAdminReply && (
                            <Chip label="管理員" size="small" color="primary" />
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {format(reply.createdAt, 'MM/dd HH:mm', { locale: zhTW })}
                          </Typography>
                        </Box>
                      }
                      secondary={reply.content}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleDeleteReply(selectedDiscussion.id, reply.id)
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>

              <Box sx={{ mt: 2 }}>
                <TextField
                  label="管理員回覆"
                  fullWidth
                  multiline
                  rows={3}
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>關閉</Button>
              <Button
                variant="contained"
                onClick={handleReply}
                disabled={!replyContent.trim() || loading}
              >
                {loading ? <CircularProgress size={24} /> : '送出回覆'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  )
}
